import {
    AddAttachmentDto,
    AddCustomerImpactDto,
    AddWatcherDto,
    AssignRequestDto,
    AttachmentDto,
    CreatePostSalesDto,
    CreatePreSalesDto,
    CrewApiClient,
    CustomerImpactDto,
    EditPostSalesDto,
    EditPreSalesDto,
    EntityType,
    HashTagDto,
    IAddAttachmentDto,
    IAddCustomerImpactDto,
    IAssignRequestDto,
    ICreatePostSalesDto,
    ICreatePreSalesDto,
    ICustomerImpactDto,
    IHashTagDto,
    IUpdateClassificationDto,
    RequestType,
    UpdateClassificationDto,
    UserDto
} from "generated/crew-backend";
import { IGetAttachment, IShareRequest } from "app/models/common/request";
import { API, RouteConstants } from "app/utils/constants";
import { useQuery } from "react-query";
import { RequestDto } from "generated/request-backend";

// The local environment has a trailing slash in the API_SERVER_URL, but test and prod do not.
const baseUrl : string = API.API_SERVER_URL.endsWith('/') ? API.API_SERVER_URL.slice(0,-1) : API.API_SERVER_URL;

//#region Queries
export const useGetAttachments = (requestData: IGetAttachment) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);
    return useQuery(
        ['useGetAttachments'],
        async () => {
            if (requestData) {
                switch (requestData.requestType) {
                    case 4:
                        return await crewApi.preSales_GetAttachments(requestData.id, requestData.entityType, requestData.portal);
                    case 5:
                    case 13:
                    case 14:
                    case 15:
                        return await crewApi.postSales_GetAttachments(requestData.id, requestData.entityType, requestData.portal);
                    default:
                        break;
                }
            }
        },
        { cacheTime: 0 }
    );
};
//#endregion queries
//#region Mutations
/**
 * Mutation used to submit shared request from unified dashboard
 */
export const useShareRequest = (data: IShareRequest) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);

    const users : UserDto[] = [];
    data.users.map(user => users.push(new UserDto({...user})));

    const attachments : AttachmentDto[] = [];
    data.attachments.map(attachment => attachments.push(new AttachmentDto({...attachment})));
    
    const dataDto : AddWatcherDto = new AddWatcherDto({
        ...data,
        users: users,
        attachments: attachments
    });

    switch (data.requestType) {
        case RequestType.PreSales:
            return crewApi.preSales_UpdateWatcher(dataDto);
        case RequestType.PostSales:
        case RequestType.CREW_WarrantyRequest:
        case RequestType.CREW_ProductRequest:
        case RequestType.CREW_MessagingRequest:
            return crewApi.postSales_UpdateWatcher(dataDto);
        default:
            // Error
            break;
    }
};

/**
 * Mutation used to submit updated request from unified dashboard
 */
export const useUpdateRequest = (data: RequestDto) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);
    const dataInterface = {
        ...data,
        // requestedResolutionDate: new Date(data.requestedResolutionDate),
        // companyInformation: new CompanyInformationDto({ ...data.companyInformation })
    }

    switch (data.requestType) {
        case RequestType.PreSales:
            let presalesDto = new EditPreSalesDto(dataInterface);
            presalesDto.init(dataInterface);
            return crewApi.preSales_UpdatePreSales(presalesDto);
        case RequestType.PostSales:
        case RequestType.CREW_WarrantyRequest:
        case RequestType.CREW_ProductRequest:
        case RequestType.CREW_MessagingRequest:
            let postSalesDto = new EditPostSalesDto(dataInterface);
            postSalesDto.init(dataInterface);
            return crewApi.postSales_UpdatePostSales(postSalesDto);
        default:
            // Error
            break;
    }
};

/**
 * Module: Admin Assign Request
 * Custom hook to assign a user to a request */
export const useAssignRequest = (requestType: RequestType, data: IAssignRequestDto) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);
    const assignDto = new AssignRequestDto(data);

    switch (requestType) {
        case RequestType.PreSales:
            return crewApi.preSales_Assign(assignDto);
        case RequestType.PostSales:
        case RequestType.CREW_WarrantyRequest:
        case RequestType.CREW_ProductRequest:
        case RequestType.CREW_MessagingRequest:
            return crewApi.postSales_Assign(assignDto);
        default:
            // Error
            break;
    }
};

/**
 * Module: Create Request
 * Used for creating new requests (e.g., aggregate parent creation) */
export const useCreateRequest = (requestType: RequestType, data: ICreatePreSalesDto | ICreatePostSalesDto) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);

    switch (requestType) {
        case RequestType.PreSales:
            const preSalesDto : CreatePreSalesDto = new CreatePreSalesDto();
            preSalesDto.init(data as ICreatePreSalesDto);
            return crewApi.preSales_CreatePreSales(preSalesDto);
        case RequestType.PostSales:
        case RequestType.CREW_WarrantyRequest:
        case RequestType.CREW_ProductRequest:
        case RequestType.CREW_MessagingRequest:
            const postSalesDto : CreatePostSalesDto = new CreatePostSalesDto();
            postSalesDto.init(data as ICreatePostSalesDto);
            return crewApi.postSales_CreatePostSales(postSalesDto);
        default:
            // Error
            break;
    }
};

export const usePostAddCustomer = (route: RouteConstants, data: IAddCustomerImpactDto) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);

    switch (route) {
        case RouteConstants.CREW_PRESALES_ADDCUSTOMERIMPACT:
            return crewApi.preSales_AddCustomerImpact(new AddCustomerImpactDto(data));
        case RouteConstants.CREW_POSTSALES_ADDCUSTOMERIMPACT:
            return crewApi.postSales_AddCustomerImpact(new AddCustomerImpactDto(data));
        default:
            // Error
            break;
    }
};

export const useAddAttachment = (route: RouteConstants, data: IAddAttachmentDto) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);

    switch (route) {
        case RouteConstants.CREW_PRESALES:
            return crewApi.preSales_AddAttachments(new AddAttachmentDto(data));
        case RouteConstants.CREW_POSTSALES:
            return crewApi.postSales_AddAttachments(new AddAttachmentDto(data));
        default:
            // Error
            break;
    }
};

export const useRemoveAttachment = (route:string, id:string, entityId?:string, entityType?:EntityType) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);

    switch (route) {
        case RouteConstants.CREW_PRESALES:
            return crewApi.preSales_DeleteAttachmentById(id, entityId, entityType);
        case RouteConstants.CREW_POSTSALES:
            return crewApi.postSales_DeleteAttachmentById(id, entityId, entityType);
        default:
            // Error
            break;
    }
};

export const useUpdateAttachmentClassification = (route:string, data:IUpdateClassificationDto) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);

    switch (route) {
        case RouteConstants.CREW_PRESALES:
            return crewApi.preSales_UpdateClassification(new UpdateClassificationDto(data));
        case RouteConstants.CREW_POSTSALES:
            return crewApi.postSales_UpdateClassification(new UpdateClassificationDto(data));
        default:
            // Error
            break;
    }
};

export const useRemoveCustomerImpact = (route:RouteConstants, id: string, data:ICustomerImpactDto) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);

    switch (route) {
        case RouteConstants.CREW_PRESALES:
            return crewApi.preSales_DeleteCustomerImpact(id, new CustomerImpactDto(data));
        case RouteConstants.CREW_POSTSALES:
            return crewApi.postSales_DeleteCustomerImpact(id, new CustomerImpactDto(data));
        default:
            // Error
            break;
    }
};

export const usePostCommentsHashTags = (route:RouteConstants, id: string, data:IHashTagDto[]) => {
    const crewApi : CrewApiClient = new CrewApiClient(baseUrl);
    const hashTags : HashTagDto[] = data.map(hashTag => {
        return new HashTagDto(hashTag)
    });

    switch (route) {
        case RouteConstants.CREW_PRESALES:
            return crewApi.preSales_AddHashTags(id, hashTags);
        case RouteConstants.CREW_POSTSALES:
            return crewApi.postSales_AddHashTags(id, hashTags);
        default:
            // Error
            break;
    }
};
//#endregion